import { LeaveAreaCode, PermissionCode, type LeavePunchKind } from '@gb-ticket/shared-types';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import {
  formatCarryOverHint,
  formatOvertimeHours,
  nextApprovedAbsence,
} from '@/products/leave/features/leave-home';
import { formatLeaveRange } from '@/products/leave/features/leave-labels';
import { berlinTodayIso } from '@/products/leave/features/leave-calendar-utils';
import { formatLeaveDays } from '@/products/leave/features/preview-vacation-days';
import { leaveAreaEnabled, useLeaveAreas } from '@/products/leave/features/use-leave-areas';
import {
  useCreateOwnLeaveOvertime,
  useConfirmLeaveTime,
  useLeaveBalance,
  useLeaveCalendar,
  useLeaveInbox,
  useLeaveRequests,
  useLeaveTime,
  usePunchLeaveTime,
} from '@/products/leave/features/use-leave';
import {
  LEAVE_INBOX_PATH,
  LEAVE_REQUEST_PATH,
  leaveRequestDetailPath,
} from '@/products/leave/leave-paths';

export function LeaveHomePage() {
  usePageTitle('Übersicht');
  const me = useMe();
  const areas = useLeaveAreas();
  const calendarOn = leaveAreaEnabled(areas.data, LeaveAreaCode.CALENDAR) === true;
  const overtimeOn = leaveAreaEnabled(areas.data, LeaveAreaCode.OVERTIME) === true;
  const timeOn = leaveAreaEnabled(areas.data, LeaveAreaCode.TIME) === true;
  const year = new Date().getFullYear();
  const today = berlinTodayIso();
  const balance = useLeaveBalance(year);
  const mine = useLeaveRequests('mine');
  const inbox = useLeaveInbox();
  const todayCalendar = useLeaveCalendar(calendarOn ? today : '', calendarOn ? today : '');
  const canSeeAll = hasPermission(me.data, PermissionCode.LEAVE_REQUESTS_DECIDE_ALL);
  const next = nextApprovedAbsence(mine.data?.items ?? [], today);
  const todayEntries = todayCalendar.data?.days[0]?.entries ?? [];
  const showInbox = canSeeAll || (inbox.data != null && inbox.data.total > 0);

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Übersicht"
        description={`Erholungsurlaub ${year}`}
        action={
          <Button asChild>
            <Link to={LEAVE_REQUEST_PATH}>Abwesenheit beantragen</Link>
          </Button>
        }
      />
      {balance.isPending ? (
        <p className="text-sm text-muted-foreground">Saldo wird geladen …</p>
      ) : null}
      {balance.isError ? (
        <p className="text-sm text-destructive">
          {balance.error instanceof ApiRequestError
            ? balance.error.message
            : 'Der Saldo konnte nicht geladen werden.'}
        </p>
      ) : null}
      {balance.data ? (
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Rest</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-semibold text-primary">
                {formatLeaveDays(balance.data.rest)}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {formatCarryOverHint(balance.data.carryOver, balance.data.carryOverExpiresOn)}
              </p>
            </CardContent>
          </Card>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Anspruch" value={formatLeaveDays(balance.data.entitlement)} />
            <Stat label="Genommen" value={formatLeaveDays(balance.data.taken)} />
            <Stat label="Beantragt" value={formatLeaveDays(balance.data.pending)} />
            {overtimeOn ? (
              <Stat label="Überstunden" value={formatOvertimeHours(balance.data.overtimeHours)} />
            ) : null}
          </div>
          {timeOn ? <WorkingTimeCard date={today} /> : null}
          {overtimeOn ? <OvertimeReportForm /> : null}
        </div>
      ) : null}
      {balance.data?.patternSource === 'default' ? (
        <p className="text-sm text-muted-foreground">
          Standardwoche Montag bis Freitag. Teilzeit trägt die Personalabteilung unter Salden ein.
        </p>
      ) : null}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Als Nächstes</CardTitle>
          </CardHeader>
          <CardContent>
            {mine.isPending ? (
              <p className="text-sm text-muted-foreground">Anträge werden geladen …</p>
            ) : next ? (
              <Link to={leaveRequestDetailPath(next.id)} className="text-sm hover:underline">
                {next.absenceType.labelDe} · {formatLeaveRange(next.startsOn, next.endsOn)}
              </Link>
            ) : (
              <p className="text-sm text-muted-foreground">Keine kommende Abwesenheit.</p>
            )}
          </CardContent>
        </Card>
        {calendarOn ? (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Heute in der Abteilung</CardTitle>
            </CardHeader>
            <CardContent>
              {todayCalendar.isPending ? (
                <p className="text-sm text-muted-foreground">Kalender wird geladen …</p>
              ) : todayCalendar.isError ? (
                <p className="text-sm text-destructive">
                  {todayCalendar.error instanceof ApiRequestError
                    ? todayCalendar.error.message
                    : 'Die Abteilung konnte nicht geladen werden.'}
                </p>
              ) : todayEntries.length === 0 ? (
                <p className="text-sm text-muted-foreground">Heute ist niemand abwesend.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {todayEntries.map((entry) => (
                    <li key={`${entry.requestId}-${entry.part}`}>
                      {entry.displayName} · {entry.labelDe}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        ) : null}
      </div>
      {showInbox ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Offen</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">
              {inbox.data?.total ?? 0} im Eingang.{' '}
              <Link to={LEAVE_INBOX_PATH} className="font-medium text-primary hover:underline">
                Zum Eingang
              </Link>
            </p>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

const PUNCH_LABEL: Record<LeavePunchKind, string> = {
  in: 'Kommen',
  out: 'Gehen',
  break_start: 'Pause begonnen',
  break_end: 'Pause beendet',
};

function WorkingTimeCard({ date }: { date: string }) {
  const status = useLeaveTime(date);
  const punch = usePunchLeaveTime(date);
  const confirm = useConfirmLeaveTime(date);

  if (status.data?.mode === 'off') {
    return null;
  }
  if (status.isPending) {
    return <p className="text-sm text-muted-foreground">Zeiterfassung wird geladen …</p>;
  }
  if (status.isError || !status.data) {
    return (
      <p className="text-sm text-destructive">Die Zeiterfassung konnte nicht geladen werden.</p>
    );
  }

  const data = status.data;

  async function stamp(kind: LeavePunchKind) {
    try {
      const next = await punch.mutateAsync({ kind });
      if (next.warnings.length > 0) {
        toast.warning(next.warnings.map((warning) => warning.message).join(' '));
      }
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Der Stempel konnte nicht gespeichert werden.',
      );
    }
  }

  async function submitSuggestion() {
    try {
      await confirm.mutateAsync();
      toast.success('Zeitdifferenz zur Genehmigung eingereicht.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Zeitdifferenz konnte nicht eingereicht werden.',
      );
    }
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Kommen / Gehen / Pause</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {data.state === 'not_started'
            ? 'Heute noch nicht begonnen.'
            : data.state === 'working'
              ? 'Arbeitszeit läuft.'
              : data.state === 'break'
                ? 'Pause läuft.'
                : 'Arbeitstag abgeschlossen.'}
        </p>
        <div className="flex flex-wrap gap-2">
          {data.state === 'not_started' || data.state === 'finished' ? (
            <Button type="button" disabled={punch.isPending} onClick={() => void stamp('in')}>
              Kommen
            </Button>
          ) : null}
          {data.state === 'working' ? (
            <>
              <Button
                type="button"
                variant="outline"
                disabled={punch.isPending}
                onClick={() => void stamp('break_start')}
              >
                Pause
              </Button>
              <Button type="button" disabled={punch.isPending} onClick={() => void stamp('out')}>
                Gehen
              </Button>
            </>
          ) : null}
          {data.state === 'break' ? (
            <Button
              type="button"
              disabled={punch.isPending}
              onClick={() => void stamp('break_end')}
            >
              Pause beenden
            </Button>
          ) : null}
        </div>
        {data.punches.length > 0 ? (
          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {data.punches.map((entry) => (
              <li key={entry.id}>
                {PUNCH_LABEL[entry.kind]} ·{' '}
                {new Intl.DateTimeFormat('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit',
                  timeZone: 'Europe/Berlin',
                }).format(new Date(entry.at))}
              </li>
            ))}
          </ul>
        ) : null}
        <p className="text-sm">
          Ist {formatOvertimeHours(data.actualHours)} · Soll {formatOvertimeHours(data.targetHours)}
        </p>
        {data.warnings.length > 0 ? (
          <ul className="space-y-1 text-sm text-amber-700 dark:text-amber-300">
            {data.warnings.map((warning) => (
              <li key={warning.code}>{warning.message}</li>
            ))}
          </ul>
        ) : null}
        {data.suggestionHours != null ? (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm">Vorschlag: {formatOvertimeHours(data.suggestionHours)}</p>
            {data.confirmed ? (
              <p className="text-sm text-muted-foreground">Zur Genehmigung eingereicht.</p>
            ) : Math.abs(data.suggestionHours) >= 0.005 ? (
              <Button
                type="button"
                variant="outline"
                disabled={confirm.isPending}
                onClick={() => void submitSuggestion()}
              >
                Vorschlag bestätigen
              </Button>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Ein Vorschlag erscheint erst nach dem Gehen.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function OvertimeReportForm() {
  const report = useCreateOwnLeaveOvertime();
  const [occurredOn, setOccurredOn] = useState(berlinTodayIso());
  const [hours, setHours] = useState('');
  const [note, setNote] = useState('');

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const parsed = Number(hours.replace(',', '.'));
    if (!Number.isFinite(parsed) || parsed < 0.25 || parsed > 999) {
      toast.error('Bitte 0,25 bis 999 Stunden eingeben.');
      return;
    }
    try {
      const row = await report.mutateAsync({
        occurredOn,
        hours: parsed,
        note: note.trim() || null,
      });
      setHours('');
      setNote('');
      toast.success('Überstunden eingereicht.');
      if (row.overtimeLimit) {
        toast.warning(
          `Das Konto würde ${formatOvertimeHours(row.overtimeLimit.projectedHours)} betragen. Die Grenze liegt bei ${formatOvertimeHours(row.overtimeLimit.maxOvertimeHours)}.`,
        );
      }
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Überstunden konnten nicht eingereicht werden.',
      );
    }
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Überstunden melden</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="space-y-3" onSubmit={(event) => void onSubmit(event)}>
          <p className="text-sm text-muted-foreground">
            Die Stunden zählen erst nach Genehmigung auf dem Konto.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="overtime-date">Datum</Label>
              <Input
                id="overtime-date"
                type="date"
                className="mt-2"
                value={occurredOn}
                onChange={(event) => setOccurredOn(event.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="overtime-hours">Stunden</Label>
              <Input
                id="overtime-hours"
                inputMode="decimal"
                className="mt-2"
                value={hours}
                onChange={(event) => setHours(event.target.value)}
                required
              />
            </div>
          </div>
          <div>
            <Label htmlFor="overtime-note">Notiz</Label>
            <Input
              id="overtime-note"
              className="mt-2"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              maxLength={300}
            />
          </div>
          <Button type="submit" disabled={report.isPending}>
            Einreichen
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-semibold text-primary">{value}</p>
      </CardContent>
    </Card>
  );
}
